import type {
  CalcKind,
  CellValue,
  ChipColor,
  FilterOperator,
  FilterRule,
  Property,
  PropertyType,
  RowBackground,
  SortRule,
  View,
} from '@noti/shared';

/**
 * Pure database logic for the table view: labels, colours, operators,
 * calculations and the filter/sort/group pipeline. No React, no fetch — this is
 * the part the tests pin down.
 */

export interface DatabaseRow {
  id: string;
  title: string;
  icon: string | null;
  /** Row background, or null when the row has none. */
  color: RowBackground | null;
  values: Record<string, CellValue>;
  createdAt: string;
  editedAt: string;
}

export const TITLE_TYPE: PropertyType = 'title';

/** Property types a user can add. `title` is created once with the data source. */
export const ADDABLE_PROPERTY_TYPES: PropertyType[] = [
  'text',
  'number',
  'select',
  'status',
  'multi',
  'person',
  'date',
  'checkbox',
  'url',
  'email',
  'phone',
  'created',
  'edited',
];

export const TYPE_LABEL: Record<PropertyType, string> = {
  title: 'Title',
  text: 'Text',
  number: 'Number',
  select: 'Select',
  status: 'Status',
  multi: 'Multi-select',
  person: 'People',
  relation: 'Relation',
  rollup: 'Rollup',
  date: 'Date',
  checkbox: 'Checkbox',
  url: 'URL',
  email: 'Email',
  phone: 'Phone',
  created: 'Created time',
  edited: 'Last edited time',
};

/** 24×24 stroke paths, straight from the prototype's PTYPE_ICON. */
export const PTYPE_ICON: Record<PropertyType, string> = {
  title: 'M5 6V4h14v2 M12 4v16 M9 20h6',
  text: 'M4 6h16 M4 12h10 M4 18h14',
  number: 'M5 9h14 M5 15h14 M10 4L8 20 M16 4l-2 16',
  select: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M8 11l4 4 4-4',
  multi: 'M8 6h13 M8 12h13 M8 18h13 M3 6l1 1 2-2 M3 12l1 1 2-2 M3 18l1 1 2-2',
  status: 'M21 12a9 9 0 1 1-9-9 M12 3v9h9',
  person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M4 21a8 8 0 0 1 16 0',
  relation: 'M10 8H6a4 4 0 0 0 0 8h4 M14 8h4a4 4 0 0 1 0 8h-4 M8 12h8',
  rollup: 'M6 4h12l-7 8 7 8H6',
  date: 'M4 6h16v14H4z M4 10h16 M8 3v4 M16 3v4',
  checkbox: 'M4 4h16v16H4z M8 12l3 3 5-6',
  url: 'M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7 M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7',
  email: 'M3 5h18v14H3z M3 7l9 6 9-6',
  phone:
    'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2',
  created: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3 2',
  edited: 'M12 20h9 M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
};

/**
 * The six option colours, as tokens so they follow the theme: the light values
 * used to be hard-coded, which made every chip a pale block in the dark theme.
 * See the chip tokens in styles/tokens.css.
 */
export const CHIP_COLOR: Record<ChipColor, { bg: string; fg: string }> = {
  grey: { bg: 'var(--color-chip-grey)', fg: 'var(--color-chip-grey-ink)' },
  mint: { bg: 'var(--color-chip-mint)', fg: 'var(--color-chip-mint-ink)' },
  green: { bg: 'var(--color-chip-green)', fg: 'var(--color-chip-green-ink)' },
  red: { bg: 'var(--color-chip-red)', fg: 'var(--color-chip-red-ink)' },
  amber: { bg: 'var(--color-chip-amber)', fg: 'var(--color-chip-amber-ink)' },
  blue: { bg: 'var(--color-chip-blue)', fg: 'var(--color-chip-blue-ink)' },
};

/** Colour cycle for options the user creates, in the prototype's order. */
export const OPTION_COLOR_CYCLE: ChipColor[] = ['mint', 'amber', 'green', 'blue', 'red', 'grey'];

/** The prototype's BG_COLS, as the token names that hold those exact colours. */
export const ROW_BACKGROUNDS: { label: string; value: RowBackground | null }[] = [
  { label: 'None', value: null },
  { label: 'Gray', value: 'gray' },
  { label: 'Yellow', value: 'yellow' },
  { label: 'Green', value: 'green' },
  { label: 'Blue', value: 'blue' },
  { label: 'Pink', value: 'pink' },
  { label: 'Red', value: 'red' },
];

export const DEFAULT_OPTIONS: Record<string, { n: string; c: ChipColor }[]> = {
  status: [
    { n: 'To do', c: 'grey' },
    { n: 'In progress', c: 'blue' },
    { n: 'Done', c: 'green' },
  ],
  select: [
    { n: 'Option 1', c: 'grey' },
    { n: 'Option 2', c: 'mint' },
    { n: 'Option 3', c: 'amber' },
  ],
  multi: [
    { n: 'Option 1', c: 'grey' },
    { n: 'Option 2', c: 'mint' },
    { n: 'Option 3', c: 'amber' },
  ],
};

const TEXT_OPERATORS: [FilterOperator, string][] = [
  ['has', 'contains'],
  ['nhas', 'does not contain'],
  ['eq', 'is'],
  ['empty', 'is empty'],
  ['nempty', 'is not empty'],
];

export const OPERATORS: Record<PropertyType, [FilterOperator, string][]> = {
  relation: TEXT_OPERATORS,
  rollup: TEXT_OPERATORS,
  title: TEXT_OPERATORS,
  text: TEXT_OPERATORS,
  url: TEXT_OPERATORS,
  email: TEXT_OPERATORS,
  phone: TEXT_OPERATORS,
  number: [
    ['eq', '='],
    ['gt', '>'],
    ['lt', '<'],
    ['empty', 'is empty'],
    ['nempty', 'is not empty'],
  ],
  select: [
    ['eq', 'is'],
    ['neq', 'is not'],
    ['empty', 'is empty'],
    ['nempty', 'is not empty'],
  ],
  status: [
    ['eq', 'is'],
    ['neq', 'is not'],
    ['empty', 'is empty'],
    ['nempty', 'is not empty'],
  ],
  person: [
    ['has', 'contains'],
    ['nhas', 'does not contain'],
    ['empty', 'is empty'],
    ['nempty', 'is not empty'],
  ],
  multi: [
    ['has', 'contains'],
    ['nhas', 'does not contain'],
    ['empty', 'is empty'],
    ['nempty', 'is not empty'],
  ],
  date: [
    ['eq', 'is'],
    ['before', 'before'],
    ['after', 'after'],
    ['empty', 'is empty'],
    ['nempty', 'is not empty'],
  ],
  created: [
    ['eq', 'is'],
    ['before', 'before'],
    ['after', 'after'],
    ['empty', 'is empty'],
    ['nempty', 'is not empty'],
  ],
  edited: [
    ['eq', 'is'],
    ['before', 'before'],
    ['after', 'after'],
    ['empty', 'is empty'],
    ['nempty', 'is not empty'],
  ],
  checkbox: [
    ['checked', 'is checked'],
    ['unchecked', 'is not checked'],
  ],
};

/** Operators that need no value control. */
export const VALUELESS_OPERATORS: FilterOperator[] = ['empty', 'nempty', 'checked', 'unchecked'];

const CALC_ALL: [CalcKind, string][] = [
  ['none', 'None'],
  ['count', 'Count all'],
  ['values', 'Count values'],
  ['empty', 'Count empty'],
  ['unique', 'Count unique'],
  ['pempty', 'Percent empty'],
  ['pfull', 'Percent not empty'],
];

const CALC_NUM: [CalcKind, string][] = [
  ['sum', 'Sum'],
  ['avg', 'Average'],
  ['median', 'Median'],
  ['min', 'Min'],
  ['max', 'Max'],
  ['range', 'Range'],
];

const CALC_DATE: [CalcKind, string][] = [
  ['earliest', 'Earliest'],
  ['latest', 'Latest'],
];

const CALC_CHECK: [CalcKind, string][] = [
  ['checked', 'Checked'],
  ['unchecked', 'Unchecked'],
  ['pchecked', 'Percent checked'],
];

export function calcOptions(type: PropertyType): [CalcKind, string][] {
  const extra: [CalcKind, string][] =
    type === 'number'
      ? CALC_NUM
      : type === 'date' || type === 'created' || type === 'edited'
        ? CALC_DATE
        : type === 'checkbox'
          ? CALC_CHECK
          : [];
  return [...CALC_ALL, ...extra];
}

/** The prototype's column widths: 220 for the title, 140 for everything else. */
export function columnWidth(property: Property, view: View): number {
  return view.widths[property.id] ?? (property.type === TITLE_TYPE ? 220 : 140);
}

export function columnWidths(properties: Property[], view: View): number[] {
  return properties.map((property) => columnWidth(property, view));
}

/** Title first, then the visible properties in schema order. */
export function visibleProperties(properties: Property[], view: View): Property[] {
  const shown = properties.filter(
    (property) => property.type === TITLE_TYPE || !view.hidden.includes(property.id),
  );
  return [
    ...shown.filter((p) => p.type === TITLE_TYPE),
    ...shown.filter((p) => p.type !== TITLE_TYPE),
  ];
}

/**
 * `grid-template-columns` for the header, every row and the calculation footer.
 * The trailing `0px` column is the gutter the row grip lives in.
 */
export function gridTemplate(widths: number[]): string {
  return `${widths.map((width) => `${width}px`).join(' ')} 0px`;
}

export function cellValue(row: DatabaseRow, property: Property): CellValue {
  if (property.type === TITLE_TYPE) return row.title;
  if (property.type === 'created') return row.createdAt;
  if (property.type === 'edited') return row.editedAt;
  return row.values[property.id] ?? null;
}

export function isEmptyValue(value: CellValue): boolean {
  return (
    value === null ||
    value === '' ||
    value === false ||
    (Array.isArray(value) && value.length === 0)
  );
}

export function asText(value: CellValue): string {
  if (value === null) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function matchesFilter(
  row: DatabaseRow,
  rule: FilterRule,
  property: Property | undefined,
): boolean {
  const value = property ? cellValue(row, property) : null;
  const text = asText(value).toLowerCase();
  const wanted = rule.val.toLowerCase();
  if (property && ['person', 'relation', 'multi'].includes(property.type) && Array.isArray(value)) {
    const contains = value.some((entry) => entry.toLowerCase() === wanted);
    if (rule.op === 'has' || rule.op === 'eq') return contains;
    if (rule.op === 'nhas' || rule.op === 'neq') return !contains;
  }
  switch (rule.op) {
    case 'has':
      return text.includes(wanted);
    case 'nhas':
      return !text.includes(wanted);
    case 'eq':
      return text === wanted;
    case 'neq':
      return text !== wanted;
    case 'gt':
      return Number(value) > Number(rule.val);
    case 'lt':
      return Number(value) < Number(rule.val);
    case 'before':
      return text !== '' && text < wanted;
    case 'after':
      return text !== '' && text > wanted;
    case 'empty':
      return isEmptyValue(value);
    case 'nempty':
      return !isEmptyValue(value);
    case 'checked':
      return value === true;
    case 'unchecked':
      return value !== true;
    default:
      return true;
  }
}

export function filterRows(
  rows: DatabaseRow[],
  rules: FilterRule[],
  mode: 'and' | 'or',
  properties: Property[],
): DatabaseRow[] {
  const usable = rules.filter((rule) => properties.some((p) => p.id === rule.propId));
  if (usable.length === 0) return rows;
  const matches = (row: DatabaseRow) => {
    const results = usable.map((rule) => {
      const property = properties.find((p) => p.id === rule.propId);
      return matchesFilter(row, rule, property);
    });
    return mode === 'or' ? results.some(Boolean) : results.every(Boolean);
  };
  return rows.filter(matches);
}

function compareValues(a: CellValue, b: CellValue, type: PropertyType): number {
  if (isEmptyValue(a)) return isEmptyValue(b) ? 0 : 1;
  if (isEmptyValue(b)) return -1;
  if (type === 'number' || (type === 'rollup' && typeof a === 'number' && typeof b === 'number'))
    return Number(a) - Number(b);
  if (type === 'checkbox') return Number(a === true) - Number(b === true);
  return asText(a).localeCompare(asText(b), 'nl');
}

export function sortRows(
  rows: DatabaseRow[],
  sorts: SortRule[],
  properties: Property[],
): DatabaseRow[] {
  const usable = sorts.filter((sort) => properties.some((p) => p.id === sort.propId));
  if (usable.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const sort of usable) {
      const property = properties.find((p) => p.id === sort.propId);
      if (!property) continue;
      const result = compareValues(cellValue(a, property), cellValue(b, property), property.type);
      if (result !== 0) return sort.dir === 'desc' ? -result : result;
    }
    return 0;
  });
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** "15 Oct 2026, 14:05" — the prototype's fmtStamp. */
export function formatStamp(value: CellValue): string {
  if (!value) return '';
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return String(value);
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${formatDate(String(value))}, ${hours}:${minutes}`;
}

/** "Oct 15" — the short form used on cards. */
export function formatShortDate(value: CellValue): string {
  if (!value) return '';
  const parsed = new Date(`${String(value)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const NO_VALUE = '–';

export function calcValue(rows: DatabaseRow[], property: Property, kind: CalcKind): string {
  const values = rows.map((row) => cellValue(row, property));
  const filled = values.filter((value) => !isEmptyValue(value));
  const percent = (count: number) =>
    rows.length === 0 ? '0%' : `${Math.round((count / rows.length) * 100)}%`;
  const nums = filled
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  const mean = (list: number[]) => list.reduce((total, value) => total + value, 0) / list.length;

  switch (kind) {
    case 'none':
      return '';
    case 'count':
      return String(rows.length);
    case 'values':
      return String(filled.length);
    case 'empty':
      return String(rows.length - filled.length);
    case 'unique':
      return String(new Set(filled.map((value) => asText(value))).size);
    case 'pempty':
      return percent(rows.length - filled.length);
    case 'pfull':
      return percent(filled.length);
    case 'sum':
      return nums.length === 0 ? NO_VALUE : String(round2(mean(nums) * nums.length));
    case 'avg':
      return nums.length === 0 ? NO_VALUE : String(round2(mean(nums)));
    case 'median':
      if (nums.length === 0) return NO_VALUE;
      return String(
        round2(
          nums.length % 2 === 1
            ? nums[(nums.length - 1) / 2]!
            : (nums[nums.length / 2 - 1]! + nums[nums.length / 2]!) / 2,
        ),
      );
    case 'min':
      return nums.length === 0 ? NO_VALUE : String(round2(nums[0]!));
    case 'max':
      return nums.length === 0 ? NO_VALUE : String(round2(nums[nums.length - 1]!));
    case 'range':
      return nums.length === 0 ? NO_VALUE : String(round2(nums[nums.length - 1]! - nums[0]!));
    case 'earliest':
    case 'latest': {
      const stamps = filled
        .map((value) => new Date(String(value)).getTime())
        .filter((time) => !Number.isNaN(time));
      if (stamps.length === 0) return NO_VALUE;
      const time = kind === 'earliest' ? Math.min(...stamps) : Math.max(...stamps);
      return formatDate(new Date(time).toISOString());
    }
    case 'checked':
      return String(values.filter((value) => value === true).length);
    case 'unchecked':
      return String(values.filter((value) => value !== true).length);
    case 'pchecked':
      return percent(values.filter((value) => value === true).length);
    default:
      return '';
  }
}

/** Only properties with a finite set of values can group rows. */
export function isGroupable(property: Property): boolean {
  return ['select', 'status', 'person', 'checkbox'].includes(property.type);
}

export interface RowGroup {
  name: string;
  chip: ChipColor;
  rows: DatabaseRow[];
  value: string;
}

/**
 * Groups in the prototype's order, with an optional trailing bucket. An empty
 * group is dropped: the table never shows a header with nothing under it. The
 * board keeps empty option columns (`keepEmptyOptions`) so cards can be dropped
 * on them; the "No value" bucket stays optional.
 */
export function groupRows(
  rows: DatabaseRow[],
  properties: Property[],
  groupPropId: string | null,
  keepEmptyOptions = false,
): RowGroup[] {
  const property = properties.find((p) => p.id === groupPropId);
  if (!property || !isGroupable(property)) {
    return [{ name: '', chip: 'grey', rows, value: '' }];
  }
  const match = (row: DatabaseRow, value: string): boolean => {
    if (property.type === 'checkbox')
      return value === 'checked'
        ? cellValue(row, property) === true
        : cellValue(row, property) !== true;
    const selected = cellValue(row, property);
    return Array.isArray(selected) ? selected.includes(value) : asText(selected) === value;
  };
  const groups: RowGroup[] =
    property.type === 'checkbox'
      ? [
          {
            name: 'Checked',
            chip: 'green',
            rows: rows.filter((row) => match(row, 'checked')),
            value: 'checked',
          },
          {
            name: 'Unchecked',
            chip: 'grey',
            rows: rows.filter((row) => match(row, 'unchecked')),
            value: 'unchecked',
          },
        ]
      : [
          ...property.options.map((option) => ({
            name: option.label ?? option.n,
            chip: option.c,
            rows: rows.filter((row) => match(row, option.n)),
            value: option.n,
          })),
          {
            name: 'No value',
            chip: 'grey',
            rows: rows.filter((row) => isEmptyValue(cellValue(row, property))),
            value: '',
          },
        ];
  return groups.filter(
    (group) => group.rows.length > 0 || (keepEmptyOptions && group.value !== ''),
  );
}

/** Rows as the table shows them: filtered, sorted, then grouped. */
export function visibleRows(
  rows: DatabaseRow[],
  properties: Property[],
  view: View,
  query: string,
): RowGroup[] {
  const searched = query.trim().toLowerCase();
  const matched = searched
    ? rows.filter((row) =>
        properties.some((property) =>
          asText(cellValue(row, property)).toLowerCase().includes(searched),
        ),
      )
    : rows;
  const filtered = filterRows(matched, view.filters, view.fmode, properties);
  const sorted = sortRows(filtered, view.sorts, properties);
  return groupRows(sorted, properties, view.group);
}

/**
 * The chips a board card, list row or gallery card shows: every visible value
 * except the title, the date (that one gets its own slot) and empty cells.
 */
export function chipsFor(
  row: DatabaseRow,
  properties: Property[],
  skip: Property[] = [],
): {
  text: string;
  chip: ChipColor;
}[] {
  const skipped = new Set(skip.map((property) => property.id));
  const chips: { text: string; chip: ChipColor }[] = [];
  for (const property of properties) {
    if (skipped.has(property.id)) continue;
    if (property.type === TITLE_TYPE || property.type === 'date') continue;
    const value = cellValue(row, property);
    if (isEmptyValue(value)) continue;
    const optionOf = (name: string) =>
      property.options.find((option) => option.n === name)?.c ?? 'grey';
    if (property.type === 'select' || property.type === 'status') {
      chips.push({ text: asText(value), chip: optionOf(asText(value)) });
      continue;
    }
    if (
      (property.type === 'multi' || property.type === 'person' || property.type === 'relation') &&
      Array.isArray(value)
    ) {
      for (const name of value)
        chips.push({
          text: property.options.find((option) => option.n === name)?.label ?? name,
          chip: optionOf(name),
        });
      continue;
    }
    if (property.type === 'created' || property.type === 'edited') {
      chips.push({ text: formatStamp(value), chip: 'grey' });
      continue;
    }
    if (property.type === 'checkbox') {
      chips.push({ text: `✓ ${property.name}`, chip: 'green' });
      continue;
    }
    chips.push({ text: asText(value), chip: 'grey' });
  }
  return chips;
}

/** The date a card shows on the right: the first visible date column. */
export function cardDate(row: DatabaseRow, properties: Property[]): string {
  const date = properties.find((property) => property.type === 'date');
  return date ? formatShortDate(cellValue(row, date)) : '';
}

/** The board column a row belongs to: its value on the first groupable column. */
export function boardValue(row: DatabaseRow, property: Property | undefined): string {
  if (!property) return '';
  if (property.type === 'checkbox')
    return cellValue(row, property) === true ? 'checked' : 'unchecked';
  return asText(cellValue(row, property));
}

/** Board columns: the groupable values plus one bucket for everything empty. */
export function boardProperty(
  properties: Property[],
  groupPropId: string | null,
): Property | undefined {
  return properties.find((p) => p.id === groupPropId) ?? properties.find(isGroupable);
}

export function boardColumns(
  rows: DatabaseRow[],
  properties: Property[],
  groupPropId: string | null,
): { name: string; chip: ChipColor; value: string; rows: DatabaseRow[] }[] {
  const property = boardProperty(properties, groupPropId);
  const groups = groupRows(rows, properties, property?.id ?? null, true);
  return groups.map((group) => ({
    name: group.name || 'All items',
    chip: group.chip,
    value: group.name,
    rows: group.rows,
  }));
}

export interface CalendarDay {
  iso: string;
  day: number;
  inMonth: boolean;
  rows: DatabaseRow[];
}

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function daysInMonth(year: number, month: number): number {
  if (month === 1) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return DAYS_IN_MONTH[month] ?? 30;
}

/** Monday-first month grid, whole weeks only, rows bucketed on the date column. */
export function monthGrid(
  year: number,
  month: number,
  properties: Property[],
  rows: DatabaseRow[],
): CalendarDay[] {
  const dateProp = properties.find((property) => property.type === 'date');
  const lead = (new Date(year, month, 1).getDay() + 6) % 7;
  const total = Math.ceil((lead + daysInMonth(year, month)) / 7) * 7;
  const days: CalendarDay[] = [];
  for (let index = 0; index < total; index += 1) {
    const number = index - lead + 1;
    const date = new Date(year, month, number);
    const inMonth = number >= 1 && number <= daysInMonth(year, month);
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate(),
    ).padStart(2, '0')}`;
    days.push({
      iso,
      // The prototype leaves the number empty outside the month (num: '').
      day: inMonth ? number : 0,
      inMonth,
      rows:
        inMonth && dateProp ? rows.filter((row) => asText(cellValue(row, dateProp)) === iso) : [],
    });
  }
  return days;
}

/** "October 2026", the calendar header. */
export function monthLabel(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/** Today as `YYYY-MM-DD` in local time, for the calendar's today marker. */
export function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}
